import type { AxiosInstance } from "axios";

import type { Configuration } from "../generated";
import {
  AuthApi,
  ProfileApi,
  NotificationApi,
  DeviceApi,
  SurveyApi,
  AnomalyApi,
  WalkApi,
  SurveyExportSettingsApi,
  PerSurveyExportSettingsApi,
  VersionApi,
} from "../generated";
import anomalyApis from "./anomalyApis";
import authApis from "./authApis";
import deviceApis from "./deviceApis";
import notificationApis from "./notificationApis";
import surveyApis from "./surveyApis";
import surveyExportSettingsApis from "./surveyExportSettingsApis";
import surveyWalkApis from "./surveyWalkApis";
import systemApis from "./systemApis";
import userApis from "./userApis";

type AuthApis = ReturnType<typeof authApis>;
type UserApis = ReturnType<typeof userApis>;
type NotificationsApis = ReturnType<typeof notificationApis>;
type DeviceApis = ReturnType<typeof deviceApis>;
type SurveyApis = ReturnType<typeof surveyApis>;
type AnomalyApis = ReturnType<typeof anomalyApis>;
type SurveyWalkApis = ReturnType<typeof surveyWalkApis>;
type SurveyExportSettingsApis = ReturnType<typeof surveyExportSettingsApis>;
type SystemApis = ReturnType<typeof systemApis>;

export default class API {
  authApi: AuthApis;
  userApi: UserApis;
  notificationsApi: NotificationsApis;
  deviceApi: DeviceApis;
  surveyApi: SurveyApis;
  anomalyApi: AnomalyApis;
  surveyWalkApi: SurveyWalkApis;
  surveyExportSettingsApi: SurveyExportSettingsApis;
  systemApi: SystemApis;

  constructor(config: Configuration, axiosInstance: AxiosInstance) {
    const authAPI = new AuthApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    const profileApi = new ProfileApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    const notificationsAPI = new NotificationApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    const deviceAPI = new DeviceApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    const surveyApi = new SurveyApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    const perSurveySettingsApi = new PerSurveyExportSettingsApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    const anomalyApi = new AnomalyApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    const surveyWalkApi = new WalkApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    const surveyExportSettingsApi = new SurveyExportSettingsApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    const versionApi = new VersionApi(
      config,
      axiosInstance.defaults.baseURL,
      axiosInstance,
    );
    this.authApi = authApis(authAPI);
    this.userApi = userApis(profileApi);
    this.notificationsApi = notificationApis(notificationsAPI);
    this.deviceApi = deviceApis(deviceAPI);
    this.surveyApi = surveyApis(surveyApi, perSurveySettingsApi);
    this.anomalyApi = anomalyApis(anomalyApi);
    this.surveyWalkApi = surveyWalkApis(surveyWalkApi);
    this.surveyExportSettingsApi = surveyExportSettingsApis(
      surveyExportSettingsApi,
    );
    this.systemApi = systemApis(versionApi);
  }

  safeAccessAPI = <T>(api: T | null): T => {
    if (!api) {
      throw new Error("API is not initialized");
    }
    return api;
  };

  getApis = () => {
    return {
      auth: this.authApi,
      device: this.deviceApi,
      user: this.userApi,
      surveyExportSettings: this.surveyExportSettingsApi,
      notifications: this.notificationsApi,
      survey: this.surveyApi,
      anomaly: this.anomalyApi,
      surveyWalk: this.surveyWalkApi,
      system: this.systemApi,
    };
  };
}
